import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  TextField,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { manufacturingAPI } from '../../services/api';

const CodeMappingDialog = ({ open, onClose, missingCodes, onMappingsCreated }) => {
  const [mappings, setMappings] = useState({});
  const [itemSearchResults, setItemSearchResults] = useState({});
  const [searchLoading, setSearchLoading] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      // Initialize empty mappings
      const initialMappings = {};
      missingCodes.forEach(code => {
        initialMappings[code] = null;
      });
      setMappings(initialMappings);
      setItemSearchResults({});
    }
  }, [open, missingCodes]);

  const searchItems = async (externalCode, searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setItemSearchResults(prev => ({ ...prev, [externalCode]: [] }));
      return;
    }

    setSearchLoading(prev => ({ ...prev, [externalCode]: true }));
    
    try {
      const response = await manufacturingAPI.searchItemsForBOM({ 
        search: searchTerm,
        exclude: []
      });
      
      let results = response.data.results || response.data;
      
      // Sort results with priority:
      // 1. Items ending in 000 (e.g., TABLE-000, CHAIR-000)
      // 2. Exact matches
      // 3. Starts with search term
      // 4. Contains search term
      results.sort((a, b) => {
        const searchUpper = searchTerm.toUpperCase();
        const aCode = a.item_code.toUpperCase();
        const bCode = b.item_code.toUpperCase();
        
        // Priority 1: Items ending in 000
        const aEndsIn000 = aCode.endsWith('000');
        const bEndsIn000 = bCode.endsWith('000');
        if (aEndsIn000 && !bEndsIn000) return -1;
        if (!aEndsIn000 && bEndsIn000) return 1;
        
        // Priority 2: Exact match
        if (aCode === searchUpper) return -1;
        if (bCode === searchUpper) return 1;
        
        // Priority 3: Starts with
        const aStarts = aCode.startsWith(searchUpper);
        const bStarts = bCode.startsWith(searchUpper);
        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;
        
        // Priority 4: Alphabetical
        return aCode.localeCompare(bCode);
      });
      
      setItemSearchResults(prev => ({ 
        ...prev, 
        [externalCode]: results 
      }));
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Failed to search items');
      setItemSearchResults(prev => ({ ...prev, [externalCode]: [] }));
    } finally {
      setSearchLoading(prev => ({ ...prev, [externalCode]: false }));
    }
  };


  const handleMappingChange = (externalCode, selectedItem) => {
    setMappings(prev => ({
      ...prev,
      [externalCode]: selectedItem
    }));
  };

  const handleSaveMappings = async () => {
    // Validate that all codes have mappings
    const unmapped = Object.entries(mappings).filter(([code, item]) => !item);
    
    if (unmapped.length > 0) {
      toast.warning(`Please map all codes. ${unmapped.length} codes still unmapped.`);
      return;
    }

    setSaving(true);
    
    try {
      // Prepare mappings data
      const mappingsData = Object.entries(mappings).map(([externalCode, item]) => ({
        external_code: externalCode,
        internal_item_id: item.id,
      }));

      // Save mappings
      const response = await manufacturingAPI.productCodeMappings.bulkCreate(mappingsData);
      
      if (response.data.success) {
        toast.success(`Created ${response.data.created} code mappings`);
        
        if (response.data.errors && response.data.errors.length > 0) {
          toast.warning(`Some mappings had errors: ${response.data.errors.length}`);
        }
        
        onMappingsCreated();
      }
    } catch (error) {
      toast.error('Failed to save mappings: ' + (error.response?.data?.error || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleSkipCode = (externalCode) => {
    setMappings(prev => {
      const newMappings = { ...prev };
      delete newMappings[externalCode];
      return newMappings;
    });
  };

  const allMapped = Object.values(mappings).every(item => item !== null);
  const mappedCount = Object.values(mappings).filter(item => item !== null).length;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinkIcon />
          <Typography variant="h6">Map Product Codes</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            The following codes from your Sage export don't match items in the system.
            Map them to existing items, and future imports will use these mappings automatically.
          </Typography>
        </Alert>

        <Box sx={{ mb: 2 }}>
          <Chip 
            label={`${mappedCount} / ${Object.keys(mappings).length} mapped`}
            color={allMapped ? 'success' : 'warning'}
            icon={allMapped ? <CheckIcon /> : null}
          />
        </Box>

        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell width="35%">Sage Code</TableCell>
                <TableCell width="50%">Maps To (Internal Item)</TableCell>
                <TableCell width="15%" align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(mappings).map(([externalCode, selectedItem]) => (
                <TableRow key={externalCode}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                      {externalCode}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Autocomplete
                      size="small"
                      options={itemSearchResults[externalCode] || []}
                      getOptionLabel={(option) => `${option.item_code} - ${option.name}`}
                      value={selectedItem}
                      onChange={(e, newValue) => handleMappingChange(externalCode, newValue)}
                      onInputChange={(e, newValue) => {
                        if (newValue && newValue.length >= 2) {
                          searchItems(externalCode, newValue);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Type to search items..."
                          variant="outlined"
                          helperText={
                            selectedItem 
                              ? null
                              : "Type at least 2 characters to search"
                          }
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {searchLoading[externalCode] && <CircularProgress color="inherit" size={20} />}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      renderOption={(props, option) => {
                        const { key, ...otherProps } = props;
                        return (
                          <li key={key} {...otherProps}>
                            <Box sx={{ width: '100%' }}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                {option.item_code}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {option.name}
                              </Typography>
                            </Box>
                          </li>
                        );
                      }}
                      noOptionsText="Type to search items..."
                      loading={searchLoading[externalCode]}
                      filterOptions={(x) => x} // Disable client-side filtering
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleSkipCode(externalCode)}
                      title="Skip this code (don't import items with this code)"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Note:</strong> Codes you skip (delete from this list) will not be imported in this batch.
            Their order lines will be skipped.
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSaveMappings}
          disabled={!allMapped || saving || Object.keys(mappings).length === 0}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          {saving ? 'Saving...' : 'Save Mappings & Continue Import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CodeMappingDialog;